import React, { useState, useEffect } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { getHolidayForDate } from '@/utils/holidays';

const FeaturedHQCard = ({ clock, format24hr, allClocks, holidays }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const timeFormat = format24hr ? 'HH:mm:ss' : 'hh:mm:ss a';
  const dateFormat = 'EEE MMM d yyyy';

  const formattedTime = formatInTimeZone(currentTime, clock.timezone, timeFormat);
  const formattedDate = formatInTimeZone(currentTime, clock.timezone, dateFormat);

  // Team availability
  const teamClocks = allClocks.filter((c) => c.id !== clock.id);
  const inBusinessHours = teamClocks.filter((c) => {
    const hour = parseInt(formatInTimeZone(currentTime, c.timezone, 'H'), 10);
    const day = parseInt(formatInTimeZone(currentTime, c.timezone, 'i'), 10);
    const isWeekday = day >= 1 && day <= 5;
    const start = c.workStartHour ?? 9;
    const end = c.workEndHour ?? 19;
    const holiday = getHolidayForDate(currentTime, c.timezone, holidays);
    return hour >= start && hour < end && isWeekday && !holiday;
  });

  // Austin status
  const austinHour = parseInt(formatInTimeZone(currentTime, clock.timezone, 'H'), 10);
  const austinDay = parseInt(formatInTimeZone(currentTime, clock.timezone, 'i'), 10);
  const austinIsWeekday = austinDay >= 1 && austinDay <= 5;
  const austinStart = clock.workStartHour ?? 9;
  const austinEnd = clock.workEndHour ?? 19;
  const austinHoliday = getHolidayForDate(currentTime, clock.timezone, holidays);
  const austinInBusiness = austinHour >= austinStart && austinHour < austinEnd && austinIsWeekday && !austinHoliday;

  const totalAvailable = inBusinessHours.length + (austinInBusiness ? 1 : 0);
  const totalTeam = teamClocks.length + 1;

  const sessionStatus = austinInBusiness ? 'ACTIVE' : 'STANDBY';
  const sessionColor = austinInBusiness ? 'text-emerald-400' : 'text-amber-400';

  return (
    <div
      className="terminal-block"
      data-testid="pinned-clock-card"
    >
      {/* Terminal title bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.08]">
        <span className="terminal-dot bg-red-500/80"></span>
        <span className="terminal-dot bg-amber-400/80"></span>
        <span className="terminal-dot bg-emerald-400/80"></span>
        <span className="ml-auto font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-zinc-500">
          world_time_control.exe
        </span>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-0">
        {/* Left: Terminal status block */}
        <div className="p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-white/[0.08] font-mono text-sm space-y-2">
          <div className="text-zinc-400">
            <span className="text-[#735AE2]">{'>'}</span> gtgs --status
          </div>
          <div>
            <span className="text-emerald-400">✓</span>{' '}
            <span className="text-zinc-400">session:</span>{' '}
            <span className={`font-semibold ${sessionColor}`} data-testid="hq-indicator">
              {austinHoliday ? `HOLIDAY (${austinHoliday.name})` : sessionStatus}
            </span>
          </div>
          <div>
            <span className="text-emerald-400">✓</span>{' '}
            <span className="text-zinc-400">location:</span>{' '}
            <span className="text-white font-semibold" data-testid="clock-name">
              AUSTIN, TEXAS
            </span>
          </div>
          <div>
            <span className="text-emerald-400">✓</span>{' '}
            <span className="text-zinc-400">local_time:</span>{' '}
            <span className="text-white font-semibold" data-testid="clock-time">
              {formattedTime}
            </span>
          </div>
          <div>
            <span className="text-emerald-400">✓</span>{' '}
            <span className="text-zinc-400">date:</span>{' '}
            <span className="text-white font-semibold" data-testid="clock-date">
              {formattedDate}
            </span>
          </div>
          <div>
            <span className="text-emerald-400">✓</span>{' '}
            <span className="text-zinc-400">working_hours:</span>{' '}
            <span className="text-white font-semibold">
              {austinStart.toString().padStart(2, '0')}:00 — {austinEnd.toString().padStart(2, '0')}:00
            </span>
          </div>
          <div>
            <span className="text-amber-400">⚡</span>{' '}
            <span className="text-zinc-400">team_available:</span>{' '}
            <span className="text-white font-semibold" data-testid="available-count">
              {totalAvailable}
            </span>
            <span className="text-zinc-500"> / {totalTeam}</span>
          </div>
          <div className="pt-3">
            <span className="text-[#735AE2]">{'>'}</span>{' '}
            <span className="text-zinc-500">structure · systems · scale</span>
          </div>
          <div>
            <span className="text-[#735AE2]">{'>'}</span>{' '}
            <span className="inline-block w-2 h-4 bg-[#735AE2] now-pulse align-middle"></span>
          </div>
        </div>

        {/* Right: Headline + big time display */}
        <div className="p-6 sm:p-8 flex flex-col justify-center">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#735AE2] mb-3">
            // headquarters
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] text-white mb-1">
            Austin.
          </h2>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] text-[#735AE2] mb-6">
            Texas.
          </h2>
          <div className="font-mono text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white" data-testid="featured-time">
            {formattedTime}
          </div>
          <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500" data-testid="hq-badge">
            // central_time · {totalAvailable}/{totalTeam} online
          </div>
        </div>
      </div>

      {/* Footer status bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-white/[0.08] font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        <span>HQ</span>
        <span className="text-zinc-700">·</span>
        <span>unmoveable</span>
        <span className="text-zinc-700">·</span>
        <span>{austinHoliday ? 'holiday detected' : austinInBusiness ? 'in business hours' : 'after hours'}</span>
      </div>
    </div>
  );
};

export default FeaturedHQCard;
